import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Offcanvas,
  ListGroup,
  Card,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "../../styles/WorkHistoryPage.css";

function NearMissPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const [records, setRecords] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRec, setEditRec] = useState(null);

  const defaultForm = {
    nm_reporter: "",
    nm_date_report: dayjs().format("YYYY-MM-DD"),
    nm_customer: "",
    nm_incident_no: "",
    nm_emp_name: "",
    nm_emp_title: "",
    nm_date_incident: dayjs().format("YYYY-MM-DD"),
    nm_time_incident: "",
    nm_location: "",
    nm_area: "",
    nm_additional_persons: "",
    nm_witnesses: "",
    nm_description: "",
    nm_emp_explanation: "",
    nm_resulting_action: "",
  };

  const [form, setForm] = useState(defaultForm);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/near-miss/${userId}`);
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrawer = (rec) => {
    setEditRec(rec);
    setForm(
      rec
        ? {
            ...rec,
            nm_date_report: dayjs(rec.nm_date_report).format("YYYY-MM-DD"),
            nm_date_incident: dayjs(rec.nm_date_incident).format("YYYY-MM-DD"),
          }
        : defaultForm
    );
    setShowDrawer(true);
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      nm_userid: userId,
    };

    try {
      if (editRec) {
        await axios.put(`${BASE_URL}/api/near-miss/${editRec.nm_id}`, payload);
        alert("Incident updated!");
      } else {
        await axios.post(`${BASE_URL}/api/near-miss`, payload);
        alert("Incident reported!");
      }

      setShowDrawer(false);
      fetchData();
    } catch (err) {
      console.error("Save error", err);
      alert("Failed to save.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/near-miss/${id}`);
      fetchData();
    } catch (err) {
      console.error("Delete error", err);
      alert("Failed to delete entry.");
    }
  };

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Near Miss Incident Report
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>
      <Container className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start">
            {/* Left side can stay empty or hold something else */}
          </div>

          <div className="col-4 text-center">
            <h3 className="mb-0">Near Miss Incident Report</h3>
          </div>

          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Card.Title className="text-center">
              Near Miss Incident Report
            </Card.Title>
            <Button variant="outline-primary" onClick={() => openDrawer(null)}>
              Add Near Miss
            </Button>

            {records.length === 0 ? (
              <p className="mt-3 text-muted fst-italic">
                No incident entries yet.
              </p>
            ) : (
              <ListGroup className="mt-3">
                {records.map((rec) => (
                  <ListGroup.Item className="work-history-item" key={rec.nm_id}>
                    <div className="work-history-company">
                      <strong>ID: </strong>
                      {rec.nm_incident_no}
                    </div>
                    <div className="work-history-dates">
                      <strong>Date: </strong>
                      {dayjs(rec.nm_date_incident).format("YYYY-MM-DD")}
                    </div>
                    <div className="work-history-actions">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => openDrawer(rec)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(rec.nm_id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>

        <Offcanvas
          show={showDrawer}
          onHide={() => setShowDrawer(false)}
          placement="start"
          scroll
          backdrop
          className="offcanvas-wide"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              {editRec ? "Edit Incident" : "New Incident"}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form className="form-box" onSubmit={handleSave}>
              <h6>Incident Information</h6>
              <Row className="mb-3">
                <Col md={6}>
                  <label>Reporter</label>
                  <input
                    className="form-control"
                    name="nm_reporter"
                    value={form.nm_reporter}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>Date Reported</label>
                  <input
                    className="form-control"
                    type="date"
                    name="nm_date_report"
                    value={form.nm_date_report}
                    onChange={handleChange}
                    required
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <label>Customer</label>
                  <input
                    className="form-control"
                    name="nm_customer"
                    value={form.nm_customer}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>Incident Number</label>
                  <input
                    className="form-control"
                    name="nm_incident_no"
                    value={form.nm_incident_no}
                    onChange={handleChange}
                    required
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <label>Employee Name</label>
                  <input
                    className="form-control"
                    name="nm_emp_name"
                    value={form.nm_emp_name}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>Employee Title</label>
                  <input
                    className="form-control"
                    name="nm_emp_title"
                    value={form.nm_emp_title}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <label>Date of Incident</label>
                  <input
                    className="form-control"
                    type="date"
                    name="nm_date_incident"
                    value={form.nm_date_incident}
                    onChange={handleChange}
                    required
                  />
                </Col>
                <Col md={6}>
                  <label>Time of Incident</label>
                  <input
                    className="form-control"
                    type="time"
                    name="nm_time_incident"
                    value={form.nm_time_incident}
                    onChange={handleChange}
                    required
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <label>Location</label>
                  <input
                    className="form-control"
                    name="nm_location"
                    value={form.nm_location}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>Area</label>
                  <input
                    className="form-control"
                    name="nm_area"
                    value={form.nm_area}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <label>Other Persons Involved</label>
                  <input
                    className="form-control"
                    name="nm_additional_persons"
                    value={form.nm_additional_persons}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <label>Witnesses</label>
                  <input
                    className="form-control"
                    name="nm_witnesses"
                    value={form.nm_witnesses}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <label>What Happened?</label>
                  <textarea
                    className="form-control"
                    name="nm_description"
                    value={form.nm_description}
                    onChange={handleChange}
                    rows={3}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <label>Employee Explanation</label>
                  <textarea
                    className="form-control"
                    name="nm_emp_explanation"
                    value={form.nm_emp_explanation}
                    onChange={handleChange}
                    rows={3}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <label>Action Taken</label>
                  <textarea
                    className="form-control"
                    name="nm_resulting_action"
                    value={form.nm_resulting_action}
                    onChange={handleChange}
                    rows={3}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-between">
                <Button
                  variant="secondary"
                  onClick={() => setShowDrawer(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Save
                </Button>
              </div>
            </Form>
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </>
  );
}

export default NearMissPage;
